import { useEffect, useMemo, useState } from 'react';
import { get, patch, post, qs } from '../lib/api';
import { navigate, useDebounced, useLoad } from '../lib/hooks';
import { markDemoStep } from '../lib/demo';
import { useSession } from '../lib/session';
import { jobWords, usePlan } from '../lib/plan';
import { formatCents } from '../../shared/money';
import { formatDateTime } from '../../shared/format';
import { Badge, Button, Confirm, Cost, Empty, Field, Money, MoneyInput, MoneyPill, Profit, Sheet, Spinner, Tabs, statTone, useAction, useToast } from '../ui/kit';
import { Icon } from '../ui/icons';
import { usePrint } from '../ui/print';
import { PAYOUT_LABELS } from '../../shared/buyback';
import { DailyReportSheet } from './DailyReport';

export type RangeKey = 'today' | 'yesterday' | 'week' | 'month' | 'last_month' | 'year' | 'last_year' | 'all' | 'custom';
const RANGES: { id: RangeKey; label: string }[] = [
  { id: 'today', label: 'Today' }, { id: 'yesterday', label: 'Yesterday' }, { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' }, { id: 'last_month', label: 'Last month' }, { id: 'year', label: 'This year' },
  { id: 'last_year', label: 'Last year' }, { id: 'all', label: 'All time' }, { id: 'custom', label: 'Pick dates' },
];

/** "2026-10-20" for a day in the store's local time (the date boxes). */
function dayStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** The start of a day typed as "2026-10-20", in local time; null for anything else. */
function dayStart(s: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const t = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
  return Number.isFinite(t) ? t : null;
}

function rangeFor(k: RangeKey, custom: { from: string; to: string }): { from: number; to: number } {
  const now = new Date();
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = day(now);
  switch (k) {
    case 'today': return { from: today.getTime(), to: Date.now() + 60_000 };
    case 'yesterday': return { from: today.getTime() - 86_400_000, to: today.getTime() };
    case 'week': return { from: today.getTime() - ((now.getDay() + 6) % 7) * 86_400_000, to: Date.now() + 60_000 };
    case 'month': return { from: new Date(now.getFullYear(), now.getMonth(), 1).getTime(), to: Date.now() + 60_000 };
    case 'last_month': return { from: new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime(), to: new Date(now.getFullYear(), now.getMonth(), 1).getTime() };
    case 'year': return { from: new Date(now.getFullYear(), 0, 1).getTime(), to: Date.now() + 60_000 };
    case 'last_year': return { from: new Date(now.getFullYear() - 1, 0, 1).getTime(), to: new Date(now.getFullYear(), 0, 1).getTime() };
    // Everything ever recorded ("total"): from the start of time to now.
    case 'all': return { from: 0, to: Date.now() + 60_000 };
    case 'custom': {
      // The two dates picked, both days included; a blank or backwards pick means today.
      const from = dayStart(custom.from);
      const toDay = dayStart(custom.to);
      if (from === null || toDay === null || toDay < from) return { from: today.getTime(), to: Date.now() + 60_000 };
      return { from, to: toDay + 86_400_000 };
    }
  }
}

/** Today, yesterday, this week… all time, or two dates: the same picker on every report. */
export function RangePicker({ range, onRange, custom, onCustom }: { range: RangeKey; onRange: (k: RangeKey) => void; custom: { from: string; to: string }; onCustom: (c: { from: string; to: string }) => void }) {
  return (
    <div className="stack-sm">
      <div className="chips">{RANGES.map((x) => <button key={x.id} type="button" className="chip" aria-pressed={range === x.id} onClick={() => onRange(x.id)}>{x.label}</button>)}</div>
      {range === 'custom' ? (
        <div className="form-grid">
          <Field label="From"><input className="input" type="date" value={custom.from} max={custom.to || undefined} onChange={(e) => onCustom({ ...custom, from: e.target.value })} /></Field>
          <Field label="To"><input className="input" type="date" value={custom.to} min={custom.from || undefined} onChange={(e) => onCustom({ ...custom, to: e.target.value })} /></Field>
        </div>
      ) : null}
    </div>
  );
}

/** The chips plus two date boxes, remembered while the Reports screen is open. */
export function useRange(initial: RangeKey = 'today') {
  const [range, setRange] = useState<RangeKey>(initial);
  const [custom, setCustom] = useState(() => ({ from: dayStr(new Date(Date.now() - 6 * 86_400_000)), to: dayStr(new Date()) }));
  const r = useMemo(() => rangeFor(range, custom), [range, custom]);
  return { range, setRange, custom, setCustom, r };
}

const METHOD: Record<string, string> = { cash: 'Cash', card: 'Card', store_credit: 'Store credit' };

/** One line of a report. `kind` puts the amount in a bubble: a cost in red, a profit in green (a loss in red, and says so). */
function Row({ k, v, strong, neg, kind }: { k: string; v: number | null | undefined; strong?: boolean; neg?: boolean; kind?: 'cost' | 'profit' }) {
  if (v === null || v === undefined) return null;
  if (kind) return <div className={`spread ${strong ? 'strong' : ''}`}><span>{k}</span><MoneyPill kind={kind} cents={v} label={kind === 'profit' && v < 0 ? 'Loss' : false} /></div>;
  return <div className={`spread ${strong ? 'strong' : ''}`}><span>{k}</span><span className="num">{neg && v ? '−' : ''}{formatCents(Math.abs(v))}</span></div>;
}

type ReportTab = 'summary' | 'days' | 'pnl' | 'wholesale';

/** Reports: the store summary, every day since the start, profit and loss, and every phone sold wholesale. */
export function ReportsScreen({ tab }: { tab?: string }) {
  const { can } = useSession();
  // Profit and loss and the wholesale history are the owner's (the server says no to anyone else too).
  const owns: Record<ReportTab, boolean> = { summary: true, days: true, pnl: can('profit.view'), wholesale: can('wholesale.sell') };
  const asked = (['summary', 'days', 'pnl', 'wholesale'].includes(tab ?? '') ? tab : 'summary') as ReportTab;
  const current: ReportTab = owns[asked] ? asked : 'summary';
  if (!can('reports.store')) return <div className="page"><Empty icon="lock" title="Reports are for managers." /></div>;
  const tabs = [{ id: 'summary', label: 'Summary' }, { id: 'days', label: 'Day by day' }, ...(owns.pnl ? [{ id: 'pnl', label: 'Profit & loss' }] : []), ...(owns.wholesale ? [{ id: 'wholesale', label: 'Wholesale history' }] : [])];
  return (
    <div className="page">
      <Tabs
        label="Reports"
        value={current}
        onChange={(t) => navigate(t === 'summary' ? '/reports' : `/reports/${t}`)}
        tabs={tabs}
      />
      {current === 'summary' ? <SummaryTab /> : null}
      {current === 'days' ? <DayByDayTab /> : null}
      {current === 'pnl' ? <PnlTab /> : null}
      {current === 'wholesale' ? <WholesaleTab /> : null}
    </div>
  );
}

/** Which store (or stores) a report is scoped to: the signed-in store, another the owner picks, or all. */
function useStoreScope() {
  const { can, me } = useSession();
  const owns = can('reports.company');
  const stores = me?.locations ?? [];
  const [store, setStore] = useState('');
  const picks = owns && stores.length > 1;
  const location = picks && store ? store : undefined;
  const storeName = store === 'all' ? 'All stores' : (store && stores.find((l) => l.id === store)?.name) || me?.location?.name || 'This store';
  const picker = picks ? (
    <div className="chips" role="group" aria-label="Which store">
      {stores.map((l) => (
        <button key={l.id} type="button" className="chip" aria-pressed={store ? store === l.id : l.id === me?.location?.id} onClick={() => setStore(l.id)}>{l.name}</button>
      ))}
      <button type="button" className="chip" aria-pressed={store === 'all'} onClick={() => setStore('all')}>All stores</button>
    </div>
  ) : null;
  return { location, storeName, picker };
}

/** Sales, tax, payments, repairs, phones bought, bill pay, activations, expenses and hours. */
function SummaryTab() {
  const { can, me } = useSession();
  // Phones, carriers' bill pay and activations are a phone shop's: other shops see them only if there's money in them.
  const plan = usePlan();
  const phones = plan.industry === 'cellphone';
  const words = jobWords(plan.industry);
  // The demo's tour ticks off "look at the numbers".
  useEffect(() => { markDemoStep('reports'); }, []);
  const { range, setRange, custom, setCustom, r } = useRange('today');
  const [daily, setDaily] = useState(false);
  const { location, storeName, picker } = useStoreScope();
  const rep = useLoad<any>(() => get(`/api/reports/summary${qs({ from: r.from, to: r.to, location })}`), [r.from, r.to, location]);
  const records = useLoad<DailyRecords>(() => get(`/api/reports/daily-records${qs({ from: r.from, to: r.to, location })}`), [r.from, r.to, location]);
  if (!can('reports.store')) return <Empty icon="lock" title="Reports are for managers." />;
  const d = rep.data;
  // The spreadsheet has the same stores as the report (All stores, or just this one).
  const csv = `/api/reports/sales.csv${qs({ from: r.from, to: r.to, location })}`;
  const profit = d ? d.after_refunds.profit_cents ?? d.sales.profit_cents : null;
  return (
    <div className="stack">
      <div className="page-head">
        <h1 style={{ marginRight: 'auto' }}>Reports · {storeName}</h1>
        {picker}
        <Button variant="primary" icon="printer" onClick={() => setDaily(true)}>Daily close-out</Button>
        <a className="btn btn-secondary" href={csv} download="sales.csv">Download sales (spreadsheet)</a>
      </div>
      {daily ? <DailyReportSheet tz={me?.settings?.timezone} onClose={() => setDaily(false)} /> : null}
      <RangePicker range={range} onRange={setRange} custom={custom} onCustom={setCustom} />
      {!d ? <Spinner /> : (
        <>
          <div className="stat-row">
            <div className="stat"><div className="k">Store sales</div><div className="v">{formatCents(d.after_refunds.net_cents)}</div></div>
            <div className="stat"><div className="k">Sales</div><div className="v">{d.sales.count}</div></div>
            <div className="stat"><div className="k">Average sale</div><div className="v">{formatCents(d.sales.average_cents)}</div></div>
            {profit !== null && profit !== undefined ? <div className={`stat ${statTone('profit', profit)}`}><div className="k">{profit < 0 ? 'Loss after refunds' : 'Profit after refunds'}</div><div className="v">{formatCents(Math.abs(profit))}</div></div> : null}
            <div className="stat"><div className="k">Tax collected</div><div className="v">{formatCents(d.after_refunds.tax_cents)}</div></div>
          </div>
          <div className="grid-2" style={{ alignItems: 'start' }}>
            <section className="card stack-sm">
              <h3>Sales</h3>
              <Row k="Sold (after discounts, before tax)" v={d.sales.net_cents} />
              <Row k="Discounts given" v={d.sales.discount_cents} neg />
              <Row k="Paid with points" v={d.sales.points_discount_cents} neg />
              <Row k="Refunds" v={d.refunds.net_cents - (d.bill_pay.refunded_cents ?? 0)} neg />
              <Row k="Store sales after refunds" v={d.after_refunds.net_cents} strong />
              {d.sales.cost_cents !== null && d.sales.cost_cents !== undefined ? (
                <>
                  <hr />
                  <Row k="Cost of what was sold" v={d.sales.cost_cents} kind="cost" />
                  {d.after_refunds.restocked_cost_cents ? <Row k="Cost of refunded items back in stock" v={d.after_refunds.restocked_cost_cents} /> : null}
                  <Row k="Gross profit after refunds" v={d.after_refunds.profit_cents ?? d.sales.profit_cents} strong kind="profit" />
                </>
              ) : null}
              <hr />
              <Row k="Taxable sales" v={d.sales.taxable_cents} />
              <Row k="Non-taxable (labor, fees)" v={d.sales.nontaxable_cents - d.bill_pay.collected_for_carriers_cents} />
              {d.sales.tax_removed?.count ? (
                <Row k={`Sold with the tax removed (${d.sales.tax_removed.count} sale${d.sales.tax_removed.count === 1 ? '' : 's'}, ${formatCents(d.sales.tax_removed.tax_cents)} tax)`} v={d.sales.tax_removed.sales_cents} />
              ) : null}
              <Row k="Tax collected (after refunds)" v={d.after_refunds.tax_cents} strong />
              <hr />
              <Row k="Accessories and products" v={d.kinds.products} />
              {phones || d.kinds.phones ? <Row k={`Phones (${d.kinds.phones_sold})`} v={d.kinds.phones} /> : null}
              <Row k={words.Jobs} v={d.kinds.repairs} />
              <Row k="Special orders" v={d.kinds.orders} />
              {phones || d.kinds.activations ? <Row k="Activation fees" v={d.kinds.activations} /> : null}
              {phones || d.kinds.billpay_fees ? <Row k="Bill pay fees" v={d.kinds.billpay_fees} /> : null}
              <Row k="Other" v={d.kinds.other} />
            </section>
            <section className="card stack-sm">
              <h3>Money in</h3>
              {d.payments.map((p: any) => <Row key={p.method} k={`${METHOD[p.method] ?? p.method} (${p.n})`} v={p.amount} />)}
              {d.refunds.by_method.map((p: any) => <Row key={`r${p.method}`} k={`Refunded by ${METHOD[p.method]?.toLowerCase() ?? p.method}`} v={p.amount} neg />)}
              <Row k="Collected" v={d.sales.collected_cents - d.refunds.total_cents} strong />
              {phones || d.bill_pay.collected_for_carriers_cents ? <p className="small muted">Includes {formatCents(d.bill_pay.collected_for_carriers_cents)} of bill payments that belong to the carriers.</p> : null}
              <h3>Cash drawer</h3>
              <div className="spread"><span>Drawers counted</span><span>{d.drawers.closed}</span></div>
              <Row k="Short" v={d.drawers.short_cents} neg />
              <Row k="Over" v={d.drawers.over_cents} />
            </section>
            <section className="card stack-sm">
              <h3>{words.Jobs}</h3>
              <div className="spread"><span>{plan.industry === 'auto' ? 'Written up' : 'Checked in'}</span><span>{d.repairs.checked_in}</span></div>
              <div className="spread"><span>{plan.industry === 'auto' ? 'Paid work orders' : 'Paid tickets'}</span><span>{d.repairs.paid_tickets}</span></div>
              <Row k="Parts" v={d.repairs.parts_cents} />
              <Row k="Labor" v={d.repairs.labor_cents} />
              {phones || d.buybacks.length ? <h3>Phones bought and trade-ins</h3> : null}
              {!phones && !d.buybacks.length ? null : d.buybacks.length ? d.buybacks.map((b: any) => (b.amount === null
                // What phones cost is the owner's: everyone else sees how many.
                ? <div key={`${b.kind}${b.payout_method}`} className="spread"><span>{`${b.kind === 'trade_in' ? 'Trade-ins' : 'Bought'} · ${PAYOUT_LABELS[b.payout_method] ?? 'Other'}`}</span><span>{b.n}</span></div>
                : <Row key={`${b.kind}${b.payout_method}`} k={`${b.kind === 'trade_in' ? 'Trade-ins' : 'Bought'} · ${PAYOUT_LABELS[b.payout_method] ?? 'Other'} (${b.n})`} v={b.amount} />)) : <p className="small muted">None.</p>}
              {d.wholesale === null || (!phones && !d.wholesale?.count) ? null : <h3>Sold wholesale</h3>}
              {d.wholesale === null || (!phones && !d.wholesale?.count) ? null : d.wholesale?.count ? (
                <>
                  <Row k={`Phones sold wholesale (${d.wholesale.count})`} v={d.wholesale.total_cents} />
                  {(d.wholesale.by_method ?? []).map((m: any) => <div key={m.method} className="spread small"><span>{m.method === 'unknown' ? 'Payment not recorded' : m.method === 'cash' ? 'Paid in cash' : `Paid by ${m.label}`} ({m.n})</span><span className="num">{formatCents(m.amount)}</span></div>)}
                  {d.wholesale.profit_cents !== null ? <Row k="Wholesale profit" v={d.wholesale.profit_cents} kind="profit" /> : null}
                </>
              ) : <p className="small muted">None.</p>}
            </section>
            {phones || d.bill_pay.count || d.activations.count ? <section className="card stack-sm">
              <h3>Bill pay (carrier money)</h3>
              <div className="spread"><span>Payments</span><span>{d.bill_pay.count}</span></div>
              <Row k="Collected for carriers" v={d.bill_pay.collected_for_carriers_cents} />
              {d.bill_pay.refunded_cents ? <Row k="Bill payments refunded" v={d.bill_pay.refunded_cents} neg /> : null}
              <Row k="Your fees" v={d.bill_pay.fees_cents} strong />
              {d.bill_pay.by_carrier.map((c: any) => <div key={c.carrier} className="spread small"><span>{c.carrier} ({c.n})</span><span className="num">{formatCents(c.amount)} · fees {formatCents(c.fees)}</span></div>)}
              <h3>Activations</h3>
              <div className="spread"><span>Activations</span><span>{d.activations.count}</span></div>
              <Row k="Activation fees" v={d.activations.fees_cents} />
              {d.activations.by_carrier.map((c: any) => <div key={c.carrier} className="spread small"><span>{c.carrier}</span><span>{c.n}</span></div>)}
            </section> : null}
            {d.commissions && (phones || d.commissions.count) ? <CommissionsCard c={d.commissions} csv={`/api/reports/commissions.csv${qs({ from: r.from, to: r.to, location })}`} /> : null}
            <section className="card stack-sm">
              <h3>By category</h3>
              {d.categories.length ? d.categories.map((c: any) => <div key={c.name} className="spread small"><span>{c.name} ({c.qty})</span><span className="num">{formatCents(c.net)}</span></div>) : <p className="small muted">No item sales.</p>}
              <h3>Top items</h3>
              {d.top_items.map((t: any) => <div key={t.description} className="spread small"><span>{t.qty} × {t.description}</span><span className="num">{formatCents(t.net)}</span></div>)}
            </section>
            <section className="card stack-sm">
              <h3>Team</h3>
              {d.people.length ? d.people.map((p: any) => <div key={p.employee_id} className="spread small"><span>{p.name} · {p.sales} sale{p.sales === 1 ? '' : 's'}{p.hours ? ` · ${p.hours.toFixed(2)} h` : ''}</span><span className="num">{formatCents(p.net_cents)}</span></div>) : <p className="small muted">No sales.</p>}
              {d.expenses ? (
                <>
                  <h3>Expenses</h3>
                  {d.expenses.rows.length ? d.expenses.rows.map((x: any) => <div key={`${x.category}${x.method}`} className="spread small"><span>{x.category} · {x.method} ({x.n})</span><span className="num">{formatCents(x.amount)}</span></div>) : <p className="small muted">None.</p>}
                  <Row k="Total expenses" v={d.expenses.total_cents} strong />
                </>
              ) : null}
              <h3>Points</h3>
              <div className="spread small"><span>Earned</span><span>{d.points.earned.toLocaleString('en-US')}</span></div>
              <div className="spread small"><span>Used</span><span>{d.points.used.toLocaleString('en-US')}</span></div>
            </section>
          </div>
          <DailyRecordsCard data={records.data ?? null} error={records.error?.message ?? null} phones={phones} />
        </>
      )}
      <SalesTaxCard />
    </div>
  );
}

// ---------------------------------------------------------------- daily records

interface DailyRecord {
  day: string; label: string;
  sales: { count: number; sold_cents: number; refunds_cents: number; store_cents: number };
  money: { cash_cents: number; card_cents: number; other_cents: number };
  wholesale: { count: number; total_cents: number; cash_cents: number; card_cents: number; other_cents: number; profit_cents: number } | null;
  /** Repairs picked up that day and what repairs brought in; activations and their fees (missing on older answers). */
  repairs?: { count: number; cents: number };
  activations?: { count: number; fees_cents: number };
}
interface DailyRecords { from: number; to: number; timezone: string; days: DailyRecord[]; totals: DailyRecord }
interface DayByDay extends DailyRecords { started: string }

/** "Thu, Oct 23, 2026" for a YYYY-MM-DD day (the day-by-day list runs back to the start, so the year matters). */
function dayWithYear(day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return day;
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12)));
}

/**
 * The day-by-day table: one row a day, cash, card (and other), the repairs picked up with the repair
 * money, the activations with their fees, wholesale for whoever sees the store's money, and the day's
 * total, with the totals of every day at the bottom. On the screen and on paper.
 */
function DayTable({ d, rows, ws, acts, other, words, paper }: { d: DayByDay; rows: DailyRecord[]; ws: boolean; acts: boolean; other: boolean; words: ReturnType<typeof jobWords>; paper?: boolean }) {
  const money = (cents: number) => (cents ? formatCents(cents) : <span className="muted">—</span>);
  const count = (n: number, cents: number) => (n ? `${n} · ${formatCents(cents)}` : <span className="muted">—</span>);
  const total = (r: DailyRecord) => r.money.cash_cents + r.money.card_cents + r.money.other_cents + (ws && r.wholesale ? r.wholesale.total_cents : 0);
  const cell = (r: DailyRecord, sum = false) => (
    <tr key={r.day} className={sum ? 'dr-total' : undefined}>
      <td className="strong">{sum ? `Total · ${d.days.length} day${d.days.length === 1 ? '' : 's'}` : dayWithYear(r.day)}</td>
      <td className="right num dr-cash">{money(r.money.cash_cents)}</td>
      <td className="right num dr-card">{money(r.money.card_cents)}</td>
      {other ? <td className="right num">{money(r.money.other_cents)}</td> : null}
      <td className="right num">{count(r.repairs?.count ?? 0, r.repairs?.cents ?? 0)}</td>
      {acts ? <td className="right num">{count(r.activations?.count ?? 0, r.activations?.fees_cents ?? 0)}</td> : null}
      {ws ? <td className="right num dr-ws">{count(r.wholesale?.count ?? 0, r.wholesale?.total_cents ?? 0)}</td> : null}
      <td className="right num strong">{money(total(r))}</td>
    </tr>
  );
  return (
    <table className={`table dr-table ${paper ? 'dbd-paper' : ''}`}>
      <thead>
        <tr>
          <th>Day</th><th className="right">Cash</th><th className="right">Card</th>{other ? <th className="right">Other</th> : null}
          <th className="right">{words.Jobs}</th>{acts ? <th className="right">Activations</th> : null}{ws ? <th className="right dr-ws">Wholesale</th> : null}<th className="right">Total</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => cell(r))}
        {cell(d.totals, true)}
      </tbody>
    </table>
  );
}

/**
 * Reports → Day by day: every day since the store started using STORE OS, one on top of the other
 * from the first day (or newest first), with cash, card, repairs, activations, wholesale (the
 * owner's) and the totals. Prints on a full page.
 */
function DayByDayTab() {
  const { can, me } = useSession();
  const plan = usePlan();
  const phones = plan.industry === 'cellphone';
  const words = jobWords(plan.industry);
  const print = usePrint();
  const { location, storeName: store, picker } = useStoreScope();
  const [newest, setNewest] = useState(false);
  const { range, setRange, custom, setCustom, r } = useRange('all');
  const data = useLoad<DayByDay>(() => get(`/api/reports/days${qs({ location, from: r.from, to: r.to })}`), [location, r.from, r.to]);
  const d = data.data;
  const ws = !!d?.totals.wholesale && (phones || d.totals.wholesale.count > 0);
  const acts = !!d && (phones || (d.totals.activations?.count ?? 0) > 0);
  const other = !!d && d.totals.money.other_cents > 0;
  const rows = d ? (newest ? [...d.days].reverse() : d.days) : [];
  // "Every day since…" for the whole history; the picked span otherwise (the days actually on the report).
  const span = d ? (range === 'all' ? `Every day since ${dayWithYear(d.started)}` : rows.length ? `${dayWithYear(rows[rows.length - 1].day)} – ${dayWithYear(rows[0].day)}` : 'No days in this range') : '';
  const paper = d ? (
    <div className="dbd-print">
      <h1>Day by day · {store}</h1>
      <p>{span} · printed {formatDateTime(Date.now(), d.timezone)}</p>
      <DayTable d={d} rows={rows} ws={ws} acts={acts} other={other} words={words} paper />
    </div>
  ) : null;
  return (
    <div className="stack">
      <div className="page-head">
        <h1 style={{ marginRight: 'auto' }}>Day by day · {store}</h1>
        {picker}
        <Button variant="ghost" onClick={() => setNewest(!newest)} aria-pressed={newest}>{newest ? 'Oldest first' : 'Newest first'}</Button>
        <Button variant="primary" icon="printer" disabled={!d} onClick={() => { if (paper) print(paper, 'letter'); }}>Print</Button>
      </div>
      <RangePicker range={range} onRange={setRange} custom={custom} onCustom={setCustom} />
      {data.error ? <div className="banner warn"><Icon.alert /><div>{data.error.message}</div></div> : null}
      {!d && !data.error ? <Spinner /> : null}
      {d ? (
        <section className="card stack-sm" aria-label="Day by day">
          <div className="spread" style={{ flexWrap: 'wrap', rowGap: 4 }}>
            <h3>{span}</h3>
            <span className="small muted">Cash and card are what came in on sales. {words.Jobs}: how many came in that day (as the goals bar counts them), and the {words.job} money taken.{acts ? ' Activations: how many, and the fees.' : ''}{ws ? ' Wholesale: phones sold, and what the buyers paid.' : ''} Total: everything that came in.</span>
          </div>
          <div className="list dr-scroll">
            <DayTable d={d} rows={rows} ws={ws} acts={acts} other={other} words={words} />
          </div>
        </section>
      ) : null}
    </div>
  );
}

/**
 * One row a day for the range: the day's sales and how they were paid (cash, card, the rest), and
 * the phones sold wholesale that day with how the buyer paid (the owner's columns), newest first,
 * with the range's totals at the bottom.
 */
function DailyRecordsCard({ data, error, phones }: { data: DailyRecords | null; error: string | null; phones: boolean }) {
  const ws = !!data?.totals.wholesale && (phones || data.totals.wholesale.count > 0);
  const money = (cents: number) => (cents ? formatCents(cents) : <span className="muted">—</span>);
  const cell = (r: DailyRecord, total = false) => (
    <tr key={r.day} className={total ? 'dr-total' : undefined}>
      <td className="strong">{r.label}</td>
      <td className="right num">{r.sales.count || <span className="muted">—</span>}</td>
      <td className="right num">{money(r.sales.store_cents)}</td>
      <td className="right num dr-cash">{money(r.money.cash_cents)}</td>
      <td className="right num dr-card">{money(r.money.card_cents)}</td>
      <td className="right num">{money(r.money.other_cents)}</td>
      {ws && r.wholesale ? (
        <>
          <td className="right num dr-ws">{r.wholesale.count || <span className="muted">—</span>}</td>
          <td className="right num dr-ws">{money(r.wholesale.total_cents)}</td>
          <td className="right num dr-ws dr-cash">{money(r.wholesale.cash_cents)}</td>
          <td className="right num dr-ws dr-card">{money(r.wholesale.card_cents)}</td>
          <td className="right num dr-ws">{money(r.wholesale.other_cents)}</td>
          <td className="right num dr-ws">{r.wholesale.count ? <Profit cents={r.wholesale.profit_cents} label={r.wholesale.profit_cents < 0 ? 'Loss' : false} /> : <span className="muted">—</span>}</td>
        </>
      ) : null}
    </tr>
  );
  return (
    <section className="card stack-sm" aria-label="Daily records">
      <div className="spread" style={{ flexWrap: 'wrap', rowGap: 4 }}>
        <h3>Daily records</h3>
        <span className="small muted">A row a day: sales by cash and card{ws ? ', and wholesale by how the buyer paid' : ''}. Pick a longer range above for more days, or see <a href="#/reports/days">every day since the start</a>.</span>
      </div>
      {error ? <div className="banner warn"><Icon.alert /><div>{error}</div></div> : null}
      {!data && !error ? <Spinner /> : null}
      {data ? (
        <div className="list dr-scroll">
          <table className="table dr-table">
            <thead>
              <tr>
                <th>Day</th><th className="right">Sales</th><th className="right">Store sales</th><th className="right">Cash</th><th className="right">Card</th><th className="right">Other</th>
                {ws ? <><th className="right dr-ws">Wholesale</th><th className="right dr-ws">Total</th><th className="right dr-ws">Cash</th><th className="right dr-ws">Card</th><th className="right dr-ws">Other</th><th className="right dr-ws">Profit</th></> : null}
              </tr>
            </thead>
            <tbody>
              {data.days.map((r) => cell(r))}
              {data.days.length > 1 ? cell(data.totals, true) : null}
            </tbody>
          </table>
        </div>
      ) : null}
      {data && !data.days.length ? <p className="small muted">No days in this range yet.</p> : null}
    </section>
  );
}

/** The months to pick from: this month and the 12 before it, newest first ("2026-10"). */
function recentMonths(): { key: string; label: string }[] {
  const out: { key: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) });
  }
  return out;
}

/** Sales tax on parts only (phones left out), for a month and the quarter so far; emailed on the 1st. */
function SalesTaxCard() {
  const industry = usePlan().industry;
  const phones = industry === 'cellphone';
  // The devices a shop sells by serial number: kept out of the parts numbers.
  const units = industry === 'computer' ? 'computers' : industry === 'vacuum' ? 'vacuums' : industry === 'auto' ? 'vehicles' : 'phones';
  const Units = units[0].toUpperCase() + units.slice(1);
  const months = useMemo(recentMonths, []);
  const [month, setMonth] = useState(months[1].key);
  const t = useLoad<any>(() => get(`/api/reports/sales-tax${qs({ month })}`), [month]);
  const { run, busy } = useAction();
  const d = t.data;
  const label = months.find((x) => x.key === month)?.label ?? month;
  const send = () => run(() => post('/api/reports/sales-tax/email', { month }), { success: `Emailed the ${label} report.` }).then((r) => { if (r) t.reload(); });
  return (
    <section className="card stack-sm" style={{ marginTop: 16 }} aria-label="Sales tax on parts">
      <div className="spread" style={{ flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ margin: 0 }}>Sales tax on parts</h3>
        <select className="select" style={{ maxWidth: 240 }} value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month">
          {months.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
        </select>
      </div>
      <p className="small muted" style={{ margin: 0 }}>{industry === 'auto' ? 'Work order parts' : 'Repair parts'}, special-order parts and parts sold from stock, after refunds.{phones ? ' Phones aren’t in it (you report those yourself).' : ''}</p>
      {!d ? <Spinner /> : (
        <div className="grid-2" style={{ alignItems: 'start' }}>
          <div className="stack-sm">
            <Row k={industry === 'auto' ? `Work order parts (${d.month.parts.repair_tickets} paid work order${d.month.parts.repair_tickets === 1 ? '' : 's'})` : `Repair parts (${d.month.parts.repair_tickets} paid repair${d.month.parts.repair_tickets === 1 ? '' : 's'})`} v={d.month.parts.repair_parts_cents} />
            <Row k="Special-order parts" v={d.month.parts.order_parts_cents} />
            <Row k="Parts sold from stock" v={d.month.parts.stock_parts_cents} />
            <Row k="Parts refunded" v={d.month.parts.refunded_cents} neg />
            <Row k="Taxable parts sales" v={d.month.parts.taxable_cents} strong />
            <Row k="Sales tax on parts" v={d.month.parts.tax_cents} strong />
            <div className="spread small muted"><span>{d.quarter.label} so far</span><span className="num">{formatCents(d.quarter.parts.taxable_cents)} · tax {formatCents(d.quarter.parts.tax_cents)}</span></div>
          </div>
          <div className="stack-sm">
            <div className="small strong muted">Not in the numbers</div>
            {d.month.parts.untaxed_cents ? <Row k="Parts sold with the tax removed (resale, out of state…)" v={d.month.parts.untaxed_cents} /> : null}
            {industry !== 'auto' || d.month.not_included.phones_sold ? <Row k={`${Units} sold (${d.month.not_included.phones_sold})`} v={d.month.not_included.phones_cents} /> : null}
            {industry !== 'auto' || d.month.not_included.phones_sold ? <Row k={`Tax collected on ${units}`} v={d.month.not_included.phones_tax_cents} /> : null}
            <Row k="Accessories and other products" v={d.month.not_included.accessories_cents} />
            {d.month.not_included.untaxed_cents ? <Row k={`${industry === 'auto' ? 'Supplies' : Units}, accessories and other items sold with the tax removed`} v={d.month.not_included.untaxed_cents} /> : null}
            <Row k={industry === 'auto' ? 'Labor (not taxed)' : 'Repair labor (not taxed)'} v={d.month.not_included.labor_cents} />
          </div>
        </div>
      )}
      {d ? (
        <>
          <div className="row-wrap">
            <Button variant="primary" icon="mail" busy={busy} disabled={!d.status.via || !d.status.email} onClick={send}>Email it{d.status.email ? ` to ${d.status.email}` : ''}</Button>
            <a className="btn btn-secondary" href={`/api/reports/sales-tax.csv${qs({ month })}`} download={`sales-tax-parts-${month}.csv`}>Download spreadsheet</a>
          </div>
          <p className="small muted" style={{ margin: 0 }}>
            {!d.status.via
              ? 'Email isn’t set up yet. Once RESEND_API_KEY (or Gmail’s SMTP settings) is added in Render, this report is emailed on the 1st of every month.'
              : d.status.schedule === 'off'
                ? 'The automatic email is off (Settings → Business & receipts).'
                : `Emailed to ${d.status.email ?? 'the owner'} on the 1st of ${d.status.schedule === 'quarterly' ? 'January, April, July and October (the whole quarter)' : 'every month'}${d.status.last_sent ? `; last one: ${d.status.last_sent}` : ''}.`}
          </p>
          {d.status.last_error ? <div className="banner bad" role="alert"><div><div className="strong">The last sales tax email didn’t go out.</div><div className="small">{d.status.last_error}</div></div></div> : null}
        </>
      ) : null}
    </section>
  );
}

const CATEGORIES = ['Supplies', 'Parts (paid locally)', 'Shipping', 'Cleaning', 'Food & drinks', 'Repairs & maintenance', 'Utilities', 'Rent', 'Payroll advance', 'Marketing', 'Other'];

/** Store expenses: cash out of the drawer (kept in the count) or paid another way. */
export function ExpensesScreen() {
  const { can } = useSession();
  const { run, busy } = useAction();
  const toast = useToast();
  const monthStart = useMemo(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1).getTime(); }, []);
  const list = useLoad<any[]>(() => get(`/api/expenses${qs({ from: monthStart })}`), []);
  const [adding, setAdding] = useState(false);
  const [voiding, setVoiding] = useState<any>(null);
  const [reason, setReason] = useState('');
  if (!can('expense.create')) return <div className="page"><Empty icon="lock" title="Expenses are for managers." /></div>;
  const total = (list.data ?? []).filter((x) => !x.voided_at).reduce((s, x) => s + x.amount_cents, 0);
  return (
    <div className="page">
      <div className="page-head">
        <h1 style={{ marginRight: 'auto' }}>Expenses</h1>
        <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>Add expense</Button>
      </div>
      <p className="muted">This month: <strong>{formatCents(total)}</strong></p>
      {!list.data ? <Spinner /> : list.data.length ? (
        <div className="list">
          {list.data.map((x) => (
            <div key={x.id} className="list-row" style={{ cursor: 'default', opacity: x.voided_at ? 0.5 : 1 }}>
              <span className="main-col"><span className="t" style={{ display: 'block' }}>{x.category}{x.vendor ? ` · ${x.vendor}` : ''}</span><span className="s" style={{ display: 'block' }}>{formatDateTime(x.created_at)} · {x.employee_name} · {x.method === 'cash' ? 'cash from the drawer' : x.method}{x.note ? ` · ${x.note}` : ''}{x.voided_at ? ` · voided: ${x.void_reason}` : ''}</span></span>
              {x.voided_at ? <Badge tone="red">Voided</Badge> : <Button size="sm" variant="ghost" onClick={() => setVoiding(x)}>Void</Button>}
              <Money cents={x.amount_cents} className="strong" />
            </div>
          ))}
        </div>
      ) : <Empty icon="cash" title="No expenses this month." />}
      {adding ? <ExpenseSheet onClose={() => setAdding(false)} onDone={() => { setAdding(false); list.reload(); }} /> : null}
      {voiding ? (
        <Confirm title="Void this expense?" confirmLabel="Void" danger busy={busy} onClose={() => setVoiding(null)} onConfirm={async () => { const r = await run(() => post<{ message?: string }>(`/api/expenses/${voiding.id}/void`, { reason })); if (r) { toast(r.message ?? 'Voided.', 'good'); setVoiding(null); setReason(''); list.reload(); } }}>
          {voiding.method === 'cash'
            ? <p>{voiding.drawer_open ? `The ${formatCents(voiding.amount_cents)} goes back into the count of the drawer it came out of.` : 'Its drawer is already closed, so no drawer count changes.'}</p>
            : null}
          <Field label="Reason"><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus /></Field>
        </Confirm>
      ) : null}
    </div>
  );
}

function ExpenseSheet({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { run, busy } = useAction();
  const [category, setCategory] = useState('Supplies');
  const [vendor, setVendor] = useState('');
  const [amount, setAmount] = useState<number | null>(null);
  const [method, setMethod] = useState<'cash' | 'card' | 'other'>('cash');
  const [note, setNote] = useState('');
  const save = async () => {
    const r = await run(() => post('/api/expenses', { category, vendor: vendor || null, amountCents: amount, method, note: note || null }), { success: 'Expense saved.' });
    if (r) onDone();
  };
  return (
    <Sheet title="Add an expense" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} disabled={!amount} onClick={save}>Save</Button></>}>
      <div className="chips">{CATEGORIES.map((c) => <button key={c} type="button" className="chip" aria-pressed={category === c} onClick={() => setCategory(c)}>{c}</button>)}</div>
      <div className="form-grid">
        <Field label="Amount"><MoneyInput value={amount} onChange={setAmount} autoFocus /></Field>
        <Field label="Paid to" hint="optional"><input className="input" value={vendor} onChange={(e) => setVendor(e.target.value)} /></Field>
      </div>
      <div className="chips">
        {([['cash', 'Cash from the drawer'], ['card', 'Card'], ['other', 'Other']] as const).map(([v, l]) => <button key={v} type="button" className="chip" aria-pressed={method === v} onClick={() => setMethod(v)}>{l}</button>)}
      </div>
      <Field label="Note" hint="optional"><input className="input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
    </Sheet>
  );
}

/**
 * Carrier commissions (owner only): what T-Mobile, AT&T, Verizon and the rest pay the store for
 * activations, by carrier, store, employee and day. The business's money, not employee pay.
 */
function CommissionsCard({ c, csv }: { c: any; csv: string }) {
  const notSet = Object.entries(c.rates ?? {}).filter(([, v]) => v === null).map(([k]) => k);
  const line = (key: string, label: string, x: any) => (
    <div key={key} className="spread small"><span>{label} · {x.count} activation{x.count === 1 ? '' : 's'}{x.waiting ? ` (${x.waiting} waiting for a rate)` : ''}</span><span className="num">{formatCents(x.cents)}</span></div>
  );
  return (
    <section className="card stack-sm" aria-label="Carrier commissions">
      <div className="spread"><h3>Carrier commissions</h3><Badge tone="plain" icon="lock">Owner only</Badge></div>
      <Row k={`Total · ${c.count} activation${c.count === 1 ? '' : 's'}`} v={c.total_cents} strong />
      {c.waiting ? <p className="small" style={{ margin: 0, color: 'var(--amber-ink, #92400e)' }}>{c.waiting} activation{c.waiting === 1 ? '' : 's'} wait for a rate ({notSet.join(', ')}). Enter it in Settings → Business & receipts and they’ll count.</p> : null}
      <h3>By carrier</h3>
      {c.by_carrier.length ? c.by_carrier.map((x: any) => line(x.carrier, x.carrier, x)) : <p className="small muted">No activations.</p>}
      {c.by_store.length > 1 ? <><h3>By store</h3>{c.by_store.map((x: any) => line(x.store, x.store, x))}</> : null}
      {c.by_employee.length ? <><h3>By employee</h3>{c.by_employee.map((x: any) => line(x.employee_id, x.name, x))}</> : null}
      {c.by_day.length > 1 ? <><h3>By day</h3>{c.by_day.map((x: any) => line(x.day, new Date(`${x.day}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }), x))}</> : null}
      <p className="small muted" style={{ margin: 0 }}>Refunded activations aren’t counted. Commissions are the business’s, not employee pay, and never on the customer’s bill.</p>
      <div><a className="btn btn-secondary btn-sm" href={csv} download="activations.csv">Download activations (spreadsheet)</a></div>
    </section>
  );
}

/** "Jan 2026" for a "2026-01" month key. */
function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * Profit and loss for any range, all time included: money in, the cost of what was sold, the
 * gross profit, then expenses (and commissions, for the owner) and the net profit, with the
 * months side by side. Only for people who see profit.
 */
function PnlTab() {
  const plan = usePlan();
  const phones = plan.industry === 'cellphone';
  const { can, me } = useSession();
  const { range, setRange, custom, setCustom, r } = useRange('month');
  const { location, storeName, picker } = useStoreScope();
  const rep = useLoad<any>(() => get(`/api/reports/pnl${qs({ from: r.from, to: r.to, location })}`), [r.from, r.to, location]);
  if (!can('reports.store') || !can('profit.view')) return <Empty icon="lock" title="Profit and loss is for people who see profit." />;
  const d = rep.data;
  const csv = `/api/reports/pnl.csv${qs({ from: r.from, to: r.to, location })}`;
  return (
    <div className="stack">
      <div className="page-head">
        <h1 style={{ marginRight: 'auto' }}>Profit & loss · {storeName}</h1>
        {picker}
        <a className="btn btn-secondary" href={csv} download="profit-and-loss.csv">Download spreadsheet</a>
      </div>
      <RangePicker range={range} onRange={setRange} custom={custom} onCustom={setCustom} />
      {rep.error ? <p className="error-text">{rep.error.message}</p> : null}
      {!d ? <Spinner /> : (
        <>
          <div className="stat-row">
            <div className="stat"><div className="k">Revenue</div><div className="v">{formatCents(d.revenue.total_cents)}</div></div>
            <div className="stat stat-cost"><div className="k">Cost of goods</div><div className="v">{formatCents(d.cost_of_goods.total_cents)}</div></div>
            <div className={`stat ${statTone('profit', d.gross_profit_cents)}`}><div className="k">{d.gross_profit_cents < 0 ? 'Gross loss' : 'Gross profit'}</div><div className="v">{formatCents(Math.abs(d.gross_profit_cents))}{d.gross_margin_pct !== null ? <span className="small muted"> · {d.gross_margin_pct}%</span> : null}</div></div>
            <div className="stat stat-cost"><div className="k">Expenses</div><div className="v">{formatCents(d.expenses.total_cents)}</div></div>
            <div className={`stat ${statTone('profit', d.net_profit_cents)}`}><div className="k">{d.net_profit_cents < 0 ? 'Net loss' : 'Net profit'}</div><div className="v">{formatCents(Math.abs(d.net_profit_cents))}</div></div>
          </div>
          <div className="grid-2" style={{ alignItems: 'start' }}>
            <section className="card stack-sm" aria-label="Money in">
              <h3>Money in</h3>
              <Row k={`Store sales (${d.revenue.sales_count}, after discounts, before tax)`} v={d.revenue.sold_cents} />
              <Row k={`Refunds (${d.revenue.refunds_count})`} v={d.revenue.refunds_cents} neg />
              <Row k="Store sales after refunds" v={d.revenue.store_sales_cents} strong />
              {phones || d.revenue.wholesale_count ? <Row k={`Phones sold wholesale (${d.revenue.wholesale_count})`} v={d.revenue.wholesale_cents} /> : null}
              <Row k="Revenue" v={d.revenue.total_cents} strong />
              <hr />
              <div className="small strong muted">What was sold</div>
              <Row k="Accessories and products" v={d.revenue.by_kind.products} />
              {phones || d.revenue.by_kind.phones ? <Row k={`Phones (${d.revenue.by_kind.phones_sold})`} v={d.revenue.by_kind.phones} /> : null}
              <Row k={`${jobWords(plan.industry).Jobs} (parts and labor)`} v={d.revenue.by_kind.repairs} />
              <Row k="Special orders" v={d.revenue.by_kind.orders} />
              {phones || d.revenue.by_kind.activations ? <Row k="Activation fees" v={d.revenue.by_kind.activations} /> : null}
              {phones || d.revenue.by_kind.billpay_fees ? <Row k="Bill pay fees" v={d.revenue.by_kind.billpay_fees} /> : null}
              {d.revenue.by_kind.deposits ? <Row k="Repair deposits (taken, less used at checkout)" v={d.revenue.by_kind.deposits} /> : null}
              <Row k="Other" v={d.revenue.by_kind.other} />
            </section>
            <section className="card stack-sm" aria-label="Cost of goods">
              <h3>Cost of goods</h3>
              <Row k="Cost of what was sold" v={d.cost_of_goods.sold_cents} kind="cost" />
              {d.cost_of_goods.restocked_cents ? <Row k="Refunded items back in stock" v={d.cost_of_goods.restocked_cents} /> : null}
              {phones || d.cost_of_goods.wholesale_cents ? <Row k="Cost of phones sold wholesale" v={d.cost_of_goods.wholesale_cents} kind="cost" /> : null}
              {d.cost_of_goods.wholesale_uncosted ? <p className="small muted" style={{ margin: 0 }}>{d.cost_of_goods.wholesale_uncosted} phone{d.cost_of_goods.wholesale_uncosted === 1 ? '' : 's'} sold wholesale with no cost on record: counted at $0.00 cost.</p> : null}
              <Row k="Cost of goods" v={d.cost_of_goods.total_cents} strong kind="cost" />
              <Row k={`Gross profit${d.gross_margin_pct !== null ? ` (${d.gross_margin_pct}% of revenue)` : ''}`} v={d.gross_profit_cents} strong kind="profit" />
              <hr />
              <h3>Expenses</h3>
              {d.expenses.rows.length ? d.expenses.rows.map((x: any) => <div key={x.category} className="spread small"><span>{x.category} ({x.n})</span><span className="num">−{formatCents(x.amount)}</span></div>) : <p className="small muted" style={{ margin: 0 }}>No expenses recorded.</p>}
              <Row k="Total expenses" v={d.expenses.total_cents} strong kind="cost" />
              {d.commissions && (phones || d.commissions.count) ? <Row k={`Carrier commissions (${d.commissions.count} activation${d.commissions.count === 1 ? '' : 's'}${d.commissions.waiting ? `, ${d.commissions.waiting} waiting for a rate` : ''})`} v={d.commissions.total_cents} /> : null}
              {d.drawers.counted ? <Row k={`Cash drawers over or short (${d.drawers.counted} counted)`} v={d.drawers.difference_cents} neg={d.drawers.difference_cents < 0} /> : null}
              <Row k="Net profit" v={d.net_profit_cents} strong kind="profit" />
            </section>
            <section className="card stack-sm" aria-label="Not in the numbers">
              <h3>Not in the numbers</h3>
              <Row k="Sales tax collected (after refunds)" v={d.other.tax_collected_cents} />
              {phones || d.other.bill_pay_count ? <Row k={`Bill payments collected for carriers (${d.other.bill_pay_count})`} v={d.other.bill_pay_cents} /> : null}
              {phones || d.other.phones_bought ? <Row k={`Phones bought from customers (${d.other.phones_bought})`} v={d.other.phones_bought_cents} /> : null}
              {phones ? <p className="small muted" style={{ margin: 0 }}>Phones bought are inventory: they count in the profit when they're sold (at the register or wholesale). Bill payments belong to the carriers; only the fees are the store's.</p> : null}
            </section>
            {d.by_month.length > 1 ? (
              <section className="card stack-sm" aria-label="By month">
                <h3>By month</h3>
                <div className="list">
                  <table className="table">
                    <thead><tr><th>Month</th><th className="right">Revenue</th><th className="right">Cost</th><th className="right">Gross</th><th className="right">Expenses</th><th className="right">Net</th></tr></thead>
                    <tbody>
                      {d.by_month.map((m: any) => (
                        <tr key={m.month}>
                          <td className="strong">{monthLabel(m.month)}</td>
                          <td className="right num">{formatCents(m.revenue_cents)}</td>
                          <td className="right num"><Cost cents={m.cost_cents} label={false} /></td>
                          <td className="right num"><Profit cents={m.gross_profit_cents} label={m.gross_profit_cents < 0 ? 'Loss' : false} /></td>
                          <td className="right num"><Cost cents={m.expenses_cents} label={false} /></td>
                          <td className="right num strong"><Profit cents={m.net_profit_cents} label={m.net_profit_cents < 0 ? 'Loss' : false} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Fix the price or cost on a phone already sold wholesale — a typo at the time, or the real cost
 * coming in later. Owner only; the server enforces that too.
 */
function WholesaleEditSheet({ w, onClose, onDone }: { w: any; onClose: () => void; onDone: () => void }) {
  const { run, busy } = useAction();
  const [price, setPrice] = useState<number | null>(w.price_cents);
  const [cost, setCost] = useState<number | null>(w.cost_cents ?? null);
  const changed = price !== w.price_cents || cost !== (w.cost_cents ?? null);
  const save = async () => {
    const r = await run(() => patch(`/api/devices/${w.device_id}/wholesale`, { priceCents: price, costCents: cost }), { success: 'Wholesale sale updated.' });
    if (r) onDone();
  };
  return (
    <Sheet
      title="Fix wholesale sale"
      onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" icon="edit" busy={busy} disabled={!price || !changed} onClick={save}>Save</Button></>}
    >
      <p className="muted">{w.title} sold wholesale to <strong>{w.buyer}</strong> on {formatDateTime(w.created_at)}. Fix the price or cost it actually sold for.</p>
      <Field label="Wholesale price"><MoneyInput value={price} onChange={setPrice} /></Field>
      <Field label="Cost (optional)"><MoneyInput value={cost} onChange={setCost} /></Field>
    </Sheet>
  );
}

/**
 * Every phone sold wholesale: when, which phone, to whom, for how much, how they paid, who sold
 * it, and the profit (for people who see costs), with totals and the buyers. Tap a row to open
 * the phone's record (Undo wholesale is there), or fix the price/cost with the edit icon.
 */
function WholesaleTab() {
  const { can, me } = useSession();
  const { range, setRange, custom, setCustom, r } = useRange('month');
  const { location, storeName, picker } = useStoreScope();
  const [q, setQ] = useState('');
  const dq = useDebounced(q.trim(), 250);
  const [editing, setEditing] = useState<any | null>(null);
  const h = useLoad<any>(() => get(`/api/reports/wholesale${qs({ from: r.from, to: r.to, location, q: dq })}`), [r.from, r.to, location, dq]);
  if (!can('inventory.view')) return <Empty icon="lock" title="Wholesale history is for people who see inventory." />;
  const d = h.data;
  const csv = `/api/reports/wholesale.csv${qs({ from: r.from, to: r.to, location, q: dq })}`;
  return (
    <div className="stack">
      <div className="page-head">
        <h1 style={{ marginRight: 'auto' }}>Wholesale history · {storeName}</h1>
        {picker}
        <a className="btn btn-secondary" href={csv} download="wholesale.csv">Download spreadsheet</a>
      </div>
      <RangePicker range={range} onRange={setRange} custom={custom} onCustom={setCustom} />
      <div className="search" style={{ maxWidth: 420 }}>
        <Icon.search />
        <input className="input" placeholder="Buyer, model or IMEI" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search wholesale sales" />
      </div>
      {h.error ? <p className="error-text">{h.error.message}</p> : null}
      {!d ? <Spinner /> : (
        <>
          <div className="stat-row">
            <div className="stat"><div className="k">Phones sold wholesale</div><div className="v">{d.totals.count}</div></div>
            <div className="stat"><div className="k">Sold for</div><div className="v">{formatCents(d.totals.total_cents)}</div></div>
            {d.costs ? <div className="stat stat-cost"><div className="k">What they cost</div><div className="v">{formatCents(d.totals.cost_cents ?? 0)}</div></div> : null}
            {d.costs ? <div className={`stat ${statTone('profit', d.totals.profit_cents ?? 0)}`}><div className="k">{(d.totals.profit_cents ?? 0) < 0 ? 'Wholesale loss' : 'Wholesale profit'}</div><div className="v">{formatCents(Math.abs(d.totals.profit_cents ?? 0))}</div></div> : null}
            {d.totals.undone ? <div className="stat"><div className="k">Undone (not counted)</div><div className="v">{d.totals.undone}</div></div> : null}
          </div>
          {d.costs && d.totals.uncosted ? <p className="small muted" style={{ margin: 0 }}>{d.totals.uncosted} phone{d.totals.uncosted === 1 ? '' : 's'} sold with no cost on record: not in the profit.</p> : null}
          {d.buyers.length ? (
            <section className="card stack-sm" aria-label="By buyer">
              <h3>By buyer</h3>
              {d.buyers.map((b: any) => (
                <div key={b.buyer} className="spread small">
                  <span>{b.buyer} · {b.count} phone{b.count === 1 ? '' : 's'} · last {formatDateTime(b.last_at)}</span>
                  <span className="num pill-row">{formatCents(b.total_cents)}{b.profit_cents !== null ? <Profit cents={b.profit_cents} /> : null}</span>
                </div>
              ))}
            </section>
          ) : null}
          {d.rows.length ? (
            <div className="list">
              <table className="table">
                <thead>
                  <tr><th>Date</th><th>Phone</th><th>Buyer</th><th>Paid by</th><th>Sold by</th><th className="right">Price</th>{d.costs ? <th className="right">Cost</th> : null}{d.costs ? <th className="right">Profit</th> : null}<th />{d.costs ? <th style={{ width: 48 }} /> : null}</tr>
                </thead>
                <tbody>
                  {d.rows.map((w: any) => (
                    <tr key={w.id} className="click" style={{ opacity: w.undone_at ? 0.55 : 1 }} onClick={() => navigate(`/inventory/devices?id=${w.device_id}&status=${w.undone_at ? 'any' : 'sold'}`)}>
                      <td className="small">{formatDateTime(w.created_at)}{location === 'all' ? <div className="muted">{w.location_name}</div> : null}</td>
                      <td><div className="strong">{w.title}</div><div className="small muted">{w.detail}</div></td>
                      <td>{w.buyer}{w.note ? <div className="small muted">{w.note}</div> : null}</td>
                      <td className="small">{w.paid_label}</td>
                      <td className="small">{w.employee_name ?? '—'}</td>
                      <td className="right strong"><Money cents={w.price_cents} /></td>
                      {d.costs ? <td className="right"><Cost cents={w.cost_cents} label={false} /></td> : null}
                      {d.costs ? <td className="right"><Profit cents={w.profit_cents} label={(w.profit_cents ?? 0) < 0 ? 'Loss' : false} /></td> : null}
                      <td>{w.undone_at ? <Badge tone="red" icon="history">Undone</Badge> : <Badge tone="green">Sold</Badge>}</td>
                      {d.costs ? (
                        <td onClick={(e) => e.stopPropagation()}>
                          {!w.undone_at ? <Button size="sm" variant="ghost" icon="edit" aria-label={`Edit the wholesale sale of ${w.title}`} title="Fix price or cost" onClick={() => setEditing(w)} /> : null}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <Empty icon="truck" title="No phones sold wholesale in this range." />}
        </>
      )}
      {editing ? <WholesaleEditSheet w={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); h.reload(); }} /> : null}
    </div>
  );
}
